import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearDoctor } from '../../../features/doctor/doctorSlice';
import './DoctorDashboard.css';
import {
  Calendar, Clock, LogOut, User, Stethoscope, CheckCircle2,
  Mail, ClipboardList, Loader2, Plus, Trash2, XCircle, CalendarPlus,
  MessageCircle, Users, Bell, Search, Send, Video, MapPin, Phone, Camera,
  AlertCircle, UserCheck,
} from 'lucide-react';
import config from '../../../config';
import AlertsBell from './AlertsBell';
import PatientReportsButton from './PatientReportsButton';
import PatientHealthButton from './PatientHealthButton';
import { formatPhoneDisplay, toDialNumber } from '../../../utils/countryCodes';

const TABS = [
  { id: 'upcoming', label: 'Upcoming', icon: Calendar },
  { id: 'pending', label: 'Pending', icon: AlertCircle },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
  { id: 'support', label: 'Support', icon: MessageCircle },
  { id: 'patients', label: 'Patients', icon: Users },
  { id: 'availability', label: 'Availability', icon: CalendarPlus },
  { id: 'cover', label: 'Cover & Invites', icon: UserCheck },
  { id: 'nurses', label: 'My Nurses', icon: ClipboardList },
  { id: 'profile', label: 'Profile', icon: User },
];

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function apiRequest(path, token, options = {}) {
  const res = await fetch(`${config.baseURL}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  const json = await res.json();
  return json.data;
}

const fetchProfile = token => apiRequest('/doctor-portal/me', token);
const updateProfile = (token, body) =>
  apiRequest('/doctor-portal/me', token, { method: 'PUT', body: JSON.stringify(body) });
const fetchTimezoneChoices = (token, country) =>
  apiRequest(`/doctor-portal/timezones?country=${encodeURIComponent(country || '')}`, token);
const fetchAppointments = (token, status) =>
  apiRequest(`/doctor-portal/appointments${status ? `?status=${status}` : ''}`, token);
const completeAppointment = (token, id, body) =>
  apiRequest(`/doctor-portal/appointments/${id}/complete`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
const cancelAppointment = (token, id) =>
  apiRequest(`/doctor-portal/appointments/${id}/cancel`, token, { method: 'PUT' });

const fetchSlots = token => apiRequest('/doctor-portal/slots', token);
const removeSlot = (token, id) =>
  apiRequest(`/doctor-portal/slots/${id}`, token, { method: 'DELETE' });
const removeAvailabilityRule = (token, id) =>
  apiRequest(`/doctor-portal/availability-rules/${id}`, token, { method: 'DELETE' });

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const PHOTO_MAX_BYTES = 2 * 1024 * 1024;
async function uploadPhoto(token, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${config.baseURL}/api/doctor-portal/me/photo`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || body.message || `Upload failed (${res.status})`);
  return body.data;
}
const removePhoto = token => apiRequest('/doctor-portal/me/photo', token, { method: 'DELETE' });

const fetchSupportTickets = (token, status) =>
  apiRequest(`/doctor-portal/support${status ? `?status=${status}` : ''}`, token);
const fetchTicketMessages = (token, ticketId) =>
  apiRequest(`/doctor-portal/support/${ticketId}/messages`, token);
const addDoctorMessage = (token, ticketId, body) =>
  apiRequest(`/doctor-portal/support/${ticketId}/messages`, token, { method: 'POST', body: JSON.stringify(body) });
const setTicketStatus = (token, ticketId, body) =>
  apiRequest(`/doctor-portal/support/${ticketId}/status`, token, { method: 'PUT', body: JSON.stringify(body) });

const fetchPatients = (token, query) =>
  apiRequest(`/doctor-portal/patients${query ? `?q=${encodeURIComponent(query)}` : ''}`, token);
const fetchPatientAppointments = (token, patientId) =>
  apiRequest(`/doctor-portal/patients/${patientId}/appointments`, token);

const createReminderForPatient = (token, body) =>
  apiRequest('/doctor-portal/reminders', token, { method: 'POST', body: JSON.stringify(body) });
const fetchPatientReminders = (token, patientId) =>
  apiRequest(`/doctor-portal/patients/${patientId}/reminders`, token);

// Nurses linked to me (read-only helpers for my patients)
const fetchNurses = token => apiRequest('/doctor-nurses', token);
const createNurse = (token, body) =>
  apiRequest('/doctor-nurses', token, { method: 'POST', body: JSON.stringify(body) });
const setNurseAccess = (token, id, body) =>
  apiRequest(`/doctor-nurses/${id}/access`, token, { method: 'PUT', body: JSON.stringify(body) });
const reinviteNurse = (token, id) =>
  apiRequest(`/doctor-nurses/${id}/reinvite`, token, { method: 'POST' });
const removeNurse = (token, id) =>
  apiRequest(`/doctor-nurses/${id}`, token, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d) {
  const datePart = String(d).slice(0, 10);
  return new Date(`${datePart}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function formatTime(t) {
  const [h, m] = t.split(':');
  const hour = ((+h + 11) % 12) + 1;
  return `${hour}:${m} ${+h < 12 ? 'AM' : 'PM'}`;
}
function formatDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function initials(name) {
  if (!name) return '?';
  return name.replace('Dr. ', '').split(' ').map(w => w[0]).join('');
}

// ---------------------------------------------------------------------------
// ConfirmModal — now also supports `details` (rich body) and `hideConfirm`
// (alert-only, single "OK" button). Both are optional; existing callers
// continue to work unchanged.
// ---------------------------------------------------------------------------
function ConfirmModal({
  title,
  message,
  details,
  confirmLabel = 'Yes',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  hideConfirm = false,
  onConfirm,
  onCancel,
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={busy ? undefined : onCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 14,
          padding: 24,
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 id="confirm-modal-title" style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700 }}>
          {title}
        </h3>

        {message && (
          <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#5F5E5A' }}>
            {message}
          </p>
        )}

        {details}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="btn-secondary"
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              background: 'white',
              color: '#3A3A38',
              fontSize: 13.5,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>

          {!hideConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={danger ? 'btn-danger' : 'btn-primary'}
              style={{ padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {busy && <Loader2 size={14} className="spin" />}
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Avatar({ name, photoUrl, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [photoUrl]);

  if (photoUrl && !failed) {
    return (
      <div className={className} style={{ padding: 0, overflow: 'hidden' }}>
        <img
          src={photoUrl}
          alt={name || 'Profile photo'}
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '50%' }}
        />
      </div>
    );
  }
  return <div className={className}>{initials(name)}</div>;
}

const APPT_TYPE_META = {
  zoom: { label: 'Zoom video call', Icon: Video },
  phone: { label: 'Phone call', Icon: Phone },
  physical: { label: 'In-person visit', Icon: MapPin },
};
function apptTypeMeta(type) {
  return APPT_TYPE_META[type] || APPT_TYPE_META.physical;
}
function telHref(phone, countryCode) {
  return `tel:${toDialNumber(countryCode, phone)}`;
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
function Sidebar({ doctor, activeTab, onTabChange, onLogout, coverTabs = [], pendingInvites = 0 }) {
  return (
    <aside className="dd-sidebar">
      <div className="dd-profile">
        <Avatar className="dd-avatar" name={doctor?.name} photoUrl={doctor?.photo_url} />
        <p className="dd-profile-name">{doctor?.name || 'Loading…'}</p>
        <p className="dd-profile-specialty">{doctor?.specialty || ''}</p>
      </div>

      <nav className="dd-nav">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              className={`dd-nav-item ${activeTab === t.id ? 'active' : ''}`}
            >
              <Icon size={16} /> {t.label}
              {t.id === 'cover' && pendingInvites > 0 && (
                <span
                  style={{
                    marginLeft: 'auto', background: '#E5484D', color: 'white', borderRadius: 999,
                    fontSize: 11, fontWeight: 700, padding: '1px 7px',
                  }}
                >
                  {pendingInvites}
                </span>
              )}
            </button>
          );
        })}

        {/* One tab per doctor I am currently covering for */}
        {coverTabs.map(c => (
          <button
            key={c.id}
            onClick={() => onTabChange(`cover:${c.id}`)}
            className={`dd-nav-item ${activeTab === `cover:${c.id}` ? 'active' : ''}`}
            title={`Covering for ${c.owner_name}`}
          >
            <UserCheck size={16} /> Covering: {c.owner_name}
          </button>
        ))}
      </nav>

      <button onClick={onLogout} className="dd-logout">
        <LogOut size={16} /> Log out
      </button>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------
function AddReminderForm({ token, patientId, onCreated }) {
  const [title, setTitle] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  function handleAddClick() {
    if (!title || !remindAt) return;
    setShowConfirm(true);
  }

  async function handleAdd() {
    setSaving(true);
    setError(null);
    try {
      await createReminderForPatient(token, { patientId, title, remindAt });
      setTitle('');
      setRemindAt('');
      setShowConfirm(false);
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.message || 'Could not add reminder');
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelConfirm() {
    if (saving) return;
    setShowConfirm(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '2 1 200px' }}>
          <input
            className="field-input"
            placeholder="e.g. Take prenatal vitamins"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <input
            type="datetime-local"
            className="field-input"
            value={remindAt}
            onChange={e => setRemindAt(e.target.value)}
          />
        </div>
        <button onClick={handleAddClick} disabled={!title || !remindAt || saving} className="btn-primary">
          <Plus size={14} /> {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
      {error && <p className="error-text" style={{ fontSize: 12, marginTop: 6 }}>{error}</p>}

      {showConfirm && (
        <ConfirmModal
          title="Add this reminder?"
          message={`"${title}" — ${formatDateTime(remindAt)}`}
          confirmLabel={saving ? 'Adding…' : 'Yes, add it'}
          busy={saving}
          onConfirm={handleAdd}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
}

function ReminderPanel({ token, patientId }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPatientReminders(token, patientId)
      .then(setReminders)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, patientId]);

  useEffect(() => { load(); }, [load]);
  
  return (
    <div className="detail-section">
      <label className="field-label"><Bell size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> Reminders</label>

      {loading ? (
        <p className="muted-text" style={{ fontSize: 13 }}>Loading…</p>
      ) : error ? (
        <p className="error-text" style={{ fontSize: 13 }}>{error}</p>
      ) : reminders.length === 0 ? (
        <p className="muted-text" style={{ fontSize: 13, marginBottom: 10 }}>No reminders yet for this patient.</p>
      ) : (
        <div className="list-stack" style={{ marginBottom: 10 }}>
          {reminders.map(r => (
            <div key={r.id} className="row-card">
              <div className="row-content">
                <p className="row-title" style={{ marginBottom: 0, textDecoration: r.is_done ? 'line-through' : 'none' }}>
                  {r.title}
                </p>
                <p className="row-meta">{formatDateTime(r.remind_at)}</p>
              </div>
              {r.created_by_doctor_id && (
                <span className="status-pill status-confirmed" style={{ fontSize: 11 }}>You added this</span>
              )}
              {r.is_done ? (
                <span className="status-pill status-completed" style={{ fontSize: 11 }}>Done</span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <AddReminderForm token={token} patientId={patientId} onCreated={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appointments
// ---------------------------------------------------------------------------
function AppointmentListItem({ appt, active, onClick, token }) {
  const typeMeta = apptTypeMeta(appt.appointment_type);
  const TypeIcon = typeMeta.Icon;
  const isRemote = appt.appointment_type === 'zoom' || appt.appointment_type === 'phone';

  return (
    <button onClick={onClick} className={`appt-list-item ${active ? 'active' : ''}`}>
      <div className="appt-list-avatar">{appt.patient_name?.[0] || '?'}</div>
      <div className="appt-list-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <p className="appt-list-name" style={{ margin: 0 }}>{appt.patient_name}</p>
          <PatientReportsButton token={token} patientId={appt.patient_id} patientName={appt.patient_name} />
          <PatientHealthButton token={token} patientId={appt.patient_id} patientName={appt.patient_name} />
        </div>
        <p className="appt-list-time">
          <Calendar size={11} /> {formatDate(appt.appointment_date)}
          <Clock size={11} style={{ marginLeft: 8 }} /> {formatTime(appt.appointment_time)}
        </p>
        {appt.appointment_type && (
          <p className="appt-list-time" style={isRemote ? { color: '#0F4C75' } : undefined}>
            <TypeIcon size={11} />
            {typeMeta.label}
          </p>
        )}
      </div>
    </button>
  );
}

function AppointmentDetail({ appt, token, onCompleted, onCancelled, isPending = false }) {
  const [notes, setNotes] = useState(appt.notes || '');
  const [followUpPlan, setFollowUpPlan] = useState(appt.follow_up_plan || '');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const isZoom = appt.appointment_type === 'zoom';
  const isPhone = appt.appointment_type === 'phone';
  const typeMeta = apptTypeMeta(appt.appointment_type);
  const TypeIcon = typeMeta.Icon;

  const patientLocal = appt.patient_timezone && appt.patient_date
    ? { date: appt.patient_date, time: appt.patient_time }
    : null;

  useEffect(() => {
    setNotes(appt.notes || '');
    setFollowUpPlan(appt.follow_up_plan || '');
  }, [appt.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCompleteClick() {
    setShowConfirmComplete(true);
  }

  async function handleComplete() {
    setSaving(true);
    setError(null);
    try {
      const updated = await completeAppointment(token, appt.id, { notes, followUpPlan });
      setShowConfirmComplete(false);
      onCompleted(updated);
    } catch (err) {
      setError(err.message || 'Could not save');
      setShowConfirmComplete(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelCompleteConfirm() {
    if (saving) return;
    setShowConfirmComplete(false);
  }

  function handleCancelClick() {
    setShowConfirmCancel(true);
  }

  async function handleCancel() {
    setCancelling(true);
    setError(null);
    try {
      await cancelAppointment(token, appt.id);
      setShowConfirmCancel(false);
      onCancelled(appt.id);
    } catch (err) {
      setError(err.message || 'Could not cancel');
      setShowConfirmCancel(false);
    } finally {
      setCancelling(false);
    }
  }

  function handleCancelCancelConfirm() {
    if (cancelling) return;
    setShowConfirmCancel(false);
  }

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <div className="detail-avatar">{appt.patient_name?.[0] || '?'}</div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h2 className="detail-name" style={{ margin: 0 }}>{appt.patient_name}</h2>
            <PatientReportsButton token={token} patientId={appt.patient_id} patientName={appt.patient_name} />
            <PatientHealthButton token={token} patientId={appt.patient_id} patientName={appt.patient_name} />
          </div>
          <p className="detail-meta"><Mail size={12} /> {appt.patient_email}</p>
        </div>
        <span className={`status-pill status-${appt.status}`}>{appt.status}</span>
      </div>

      {isPending && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', marginBottom: 14,
            background: '#FFF4E5', border: '1px solid #F3C98B',
            borderRadius: 8, color: '#8A5A00', fontSize: 13,
          }}
        >
          <AlertCircle size={15} style={{ flexShrink: 0 }} />
          <span>
            This appointment's scheduled time was over 24 hours ago and you haven't
            marked it complete or cancelled it yet. Please respond below.
          </span>
        </div>
      )}

      <div className="detail-row">
        <span className="detail-label">Scheduled</span>
        <span className="detail-value">
          {formatDate(appt.appointment_date)} · {formatTime(appt.appointment_time)}
          {appt.timezone_label ? ` · ${appt.timezone_label}` : ''}
        </span>
      </div>

      {patientLocal && (
        <div className="detail-row">
          <span className="detail-label">Patient's time</span>
          <span className="detail-value">
            {formatDate(patientLocal.date)} · {formatTime(patientLocal.time)} · {appt.patient_timezone_label}
          </span>
        </div>
      )}

      {appt.appointment_type && (
        <div className="detail-row">
          <span className="detail-label">Type</span>
          <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <TypeIcon size={13} /> {typeMeta.label}
          </span>
        </div>
      )}

      {isZoom && appt.meeting_link && appt.status !== 'cancelled' && (
        <div className="detail-row">
          <span className="detail-label">Zoom link</span>
          <span className="detail-value">
            <a
              href={appt.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0F4C75', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Video size={13} /> Join meeting
            </a>
          </span>
        </div>
      )}

      {isPhone && appt.status !== 'cancelled' && (
        <div className="detail-row">
          <span className="detail-label">Call patient</span>
          <span className="detail-value">
            {appt.patient_phone ? (
              <a
                href={telHref(appt.patient_phone, appt.patient_phone_country_code)}
                style={{ color: '#0F4C75', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Phone size={13} /> {formatPhoneDisplay({ phone: appt.patient_phone, phoneCountryCode: appt.patient_phone_country_code })}
              </a>
            ) : (
              <span className="muted-text">No phone number on file for this patient</span>
            )}
          </span>
        </div>
      )}

      <div className="detail-section">
        <label className="field-label">Consultation notes</label>
        <textarea
          className="field-input"
          rows={4}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="What was discussed, observations, recommendations…"
          disabled={appt.status === 'completed'}
        />
      </div>

      <div className="detail-section">
        <label className="field-label">Follow-up plan</label>
        <textarea
          className="field-input"
          rows={3}
          value={followUpPlan}
          onChange={e => setFollowUpPlan(e.target.value)}
          placeholder="Next steps for the patient…"
          disabled={appt.status === 'completed'}
        />
      </div>

      {error && <p className="error-text">{error}</p>}

      {appt.status === 'confirmed' && (
        <div className="detail-actions">
          <button onClick={handleCompleteClick} disabled={saving || cancelling} className="btn-primary">
            <ClipboardList size={15} /> {saving ? 'Saving…' : 'Mark visit complete'}
          </button>
          <button onClick={handleCancelClick} disabled={saving || cancelling} className="btn-danger">
            <XCircle size={15} /> {cancelling ? 'Cancelling…' : 'Cancel appointment'}
          </button>
        </div>
      )}

      <ReminderPanel token={token} patientId={appt.patient_id} />

      {showConfirmComplete && (
        <ConfirmModal
          title="Mark this visit complete?"
          message="This saves your consultation notes and follow-up plan, and moves the appointment to Completed."
          confirmLabel={saving ? 'Saving…' : 'Yes, mark complete'}
          busy={saving}
          onConfirm={handleComplete}
          onCancel={handleCancelCompleteConfirm}
        />
      )}

      {showConfirmCancel && (
        <ConfirmModal
          title="Cancel this appointment?"
          message="This frees up the time slot and cannot be undone."
          confirmLabel={cancelling ? 'Cancelling…' : 'Yes, cancel it'}
          danger
          busy={cancelling}
          onConfirm={handleCancel}
          onCancel={handleCancelCancelConfirm}
        />
      )}
    </div>
  );
}

function AppointmentsView({ token, statusFilter }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAppointments(token, statusFilter)
      .then(rows => {
        setItems(rows);
        setSelectedId(rows[0]?.id ?? null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, statusFilter]);

  useEffect(() => { load(); }, [load]);

  function handleCompleted(updated) {
    if (statusFilter === 'confirmed' || statusFilter === 'pending') {
      setItems(prev => prev.filter(a => a.id !== updated.id));
      setSelectedId(prev => (items.find(a => a.id !== updated.id)?.id ?? null));
    } else {
      setItems(prev => prev.map(a => (a.id === updated.id ? { ...a, ...updated } : a)));
    }
  }

  function handleCancelled(id) {
    setItems(prev => {
      const next = prev.filter(a => a.id !== id);
      setSelectedId(next[0]?.id ?? null);
      return next;
    });
  }

  if (loading) {
    return <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>;
  }
  if (error) return <p className="error-text">{error}</p>;
  if (items.length === 0) return <p className="muted-text">Nothing here yet.</p>;

  const selected = items.find(a => a.id === selectedId) || items[0];

  return (
    <div className="master-detail">
      <div className="appt-list-col">
        {items.map(a => (
          <AppointmentListItem key={a.id} appt={a} active={a.id === selected.id} onClick={() => setSelectedId(a.id)} token={token} />
        ))}
      </div>
      <div className="detail-col">
        {selected && (
          <AppointmentDetail
            appt={selected}
            token={token}
            onCompleted={handleCompleted}
            onCancelled={handleCancelled}
            isPending={statusFilter === 'pending'}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
function ProfileView({ doctor, token, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState(doctor?.bio || '');
  const [address, setAddress] = useState(doctor?.address || '');
  const [country, setCountry] = useState(doctor?.country || '');
  const [timezone, setTimezone] = useState(doctor?.effective_timezone || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const photoInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  useEffect(() => {
    setBio(doctor?.bio || '');
    setAddress(doctor?.address || '');
    setCountry(doctor?.country || '');
    setTimezone(doctor?.effective_timezone || '');
  }, [doctor]);

  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  const [choices, setChoices] = useState(doctor?.timezone_choices || null);
  useEffect(() => { setChoices(doctor?.timezone_choices || null); }, [doctor]);

  async function handleCountryChange(value) {
    setCountry(value);
    try {
      const next = await fetchTimezoneChoices(token, value);
      setChoices(next);
      const own = next.groups[0]?.label.startsWith('Timezones in') ? next.groups[0].zones.map(z => z.value) : [];
      if (own.length > 0 && !own.includes(timezone) && next.suggested) setTimezone(next.suggested);
    } catch (e) { /* keep what's shown */ }
  }

  if (!doctor) return <p className="muted-text">Loading profile…</p>;

  function handleSaveClick() {
    setError(null);
    setSuccessMsg(null);
    setShowConfirmSave(true);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProfile(token, { bio, address, country, timezone });
      onUpdated(updated);
      setEditing(false);
      setShowConfirmSave(false);
      setSuccessMsg('Your profile was updated successfully.');
    } catch (err) {
      setError(err.message || 'Could not save profile');
      setShowConfirmSave(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelSaveConfirm() {
    if (saving) return;
    setShowConfirmSave(false);
  }

  async function handlePhotoPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setPhotoError('Please choose a JPG, PNG or GIF image.');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setPhotoError('Image must be 2 MB or smaller.');
      return;
    }
    setPhotoBusy(true);
    setPhotoError(null);
    setSuccessMsg(null);
    try {
      onUpdated(await uploadPhoto(token, file));
      setSuccessMsg('Profile photo updated.');
    } catch (err) {
      setPhotoError(err.message || 'Could not upload photo');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function handlePhotoRemove() {
    if (!window.confirm('Remove your profile photo?')) return;
    setPhotoBusy(true);
    setPhotoError(null);
    setSuccessMsg(null);
    try {
      onUpdated(await removePhoto(token));
      setSuccessMsg('Profile photo removed.');
    } catch (err) {
      setPhotoError(err.message || 'Could not remove photo');
    } finally {
      setPhotoBusy(false);
    }
  }

  function handleCancel() {
    setBio(doctor.bio || '');
    setAddress(doctor.address || '');
    setCountry(doctor.country || '');
    setTimezone(doctor.effective_timezone || '');
    setError(null);
    setEditing(false);
  }

  return (
    <div className="detail-panel" style={{ maxWidth: 480 }}>
      <div className="detail-header">
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar className="detail-avatar" name={doctor.name} photoUrl={doctor.photo_url} />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={photoBusy}
            aria-label="Change profile photo"
            title="Change profile photo"
            style={{
              position: 'absolute', right: -4, bottom: -4, width: 26, height: 26, borderRadius: '50%',
              border: '2px solid white', background: '#0F4C75', color: 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            }}
          >
            {photoBusy ? <Loader2 size={12} className="spin" /> : <Camera size={12} />}
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handlePhotoPicked}
            style={{ display: 'none' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="detail-name">{doctor.name}</h2>
          <p className="detail-meta"><Stethoscope size={12} /> {doctor.specialty}</p>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoBusy}
              style={{ background: 'none', border: 'none', padding: 0, color: '#0F4C75', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline' }}
            >
              {photoBusy ? 'Uploading…' : doctor.photo_url ? 'Change photo' : 'Upload photo'}
            </button>
            {doctor.photo_url && !photoBusy && (
              <button
                type="button"
                onClick={handlePhotoRemove}
                style={{ background: 'none', border: 'none', padding: 0, color: '#B42318', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline' }}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {!editing && (
          <button
            onClick={() => { setSuccessMsg(null); setEditing(true); }}
            className="btn-primary"
            style={{ background: 'white', color: '#0F4C75', border: '1px solid #0F4C75' }}
          >
            Edit
          </button>
        )}
      </div>

      {photoError && <p className="error-text" style={{ fontSize: 12.5 }}>{photoError}</p>}

      {successMsg && (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', margin: '8px 0',
            background: '#E7F6EC', border: '1px solid #9BD4AE',
            borderRadius: 8, color: '#1B6B3A', fontSize: 13,
          }}
        >
          <CheckCircle2 size={15} style={{ flexShrink: 0 }} />
          <span>{successMsg}</span>
        </div>
      )}

      <div className="detail-row"><span className="detail-label">Email</span><span className="detail-value">{doctor.email}</span></div>

      {editing ? (
        <>
          <div className="detail-section">
            <label className="field-label">Bio</label>
            <textarea className="field-input" rows={3} value={bio} onChange={e => setBio(e.target.value)} placeholder="A short professional bio…" />
          </div>
          <div className="detail-section">
            <label className="field-label">Address</label>
            <input className="field-input" value={address} onChange={e => setAddress(e.target.value)} placeholder="Clinic or office address" />
          </div>
          <div className="detail-section">
            <label className="field-label">Country</label>
            <input className="field-input" value={country} onChange={e => handleCountryChange(e.target.value)} placeholder="Country" />
          </div>
          <div className="detail-section">
            <label className="field-label">Timezone</label>
            <select className="field-input" aria-label="Timezone" value={timezone} onChange={e => setTimezone(e.target.value)}>
              {(choices?.groups || []).map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.zones.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                </optgroup>
              ))}
            </select>
            <p className="muted-text" style={{ fontSize: 12, marginTop: 6 }}>
              Your availability runs on this timezone. Patients see your times converted to their own.
            </p>
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="detail-actions">
            <button onClick={handleSaveClick} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={handleCancel} disabled={saving} className="btn-danger">Cancel</button>
          </div>
        </>
      ) : (
        <>
          {doctor.bio && (
            <div className="detail-section">
              <label className="field-label">Bio</label>
              <p className="row-desc">{doctor.bio}</p>
            </div>
          )}
          {doctor.address && (
            <div className="detail-row">
              <span className="detail-label">Address</span>
              <span className="detail-value">{doctor.address}</span>
            </div>
          )}
          {doctor.country && (
            <div className="detail-row">
              <span className="detail-label">Country</span>
              <span className="detail-value">{doctor.country}</span>
            </div>
          )}
          {doctor.effective_timezone && (
            <>
              <div className="detail-row">
                <span className="detail-label">Timezone</span>
                <span className="detail-value">{doctor.effective_timezone_label}</span>
              </div>
              {doctor.timezone_source === 'country' && (
                <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>
                  Based on your country ({doctor.country}). Click Edit to confirm your exact timezone.
                </p>
              )}
              {doctor.timezone_source === 'default' && (
                <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>
                  Your country isn't recognised, so a default timezone is used. Click Edit to set your country or timezone.
                </p>
              )}
            </>
          )}
          {!doctor.bio && !doctor.address && !doctor.country && (
            <p className="muted-text" style={{ marginTop: 12 }}>No bio, address, or country set yet — click Edit to add them.</p>
          )}
        </>
      )}

      {showConfirmSave && (
        <ConfirmModal
          title="Save these changes?"
          message="Your bio, address, country and timezone will be updated. Patients will see your availability in the new timezone."
          confirmLabel={saving ? 'Saving…' : 'Yes, save changes'}
          busy={saving}
          onConfirm={handleSave}
          onCancel={handleCancelSaveConfirm}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Support
// ---------------------------------------------------------------------------
function SupportTicketListItem({ ticket, active, onClick }) {
  return (
    <button onClick={onClick} className={`appt-list-item ${active ? 'active' : ''}`}>
      <div className="appt-list-avatar">{ticket.patient_name?.[0] || '?'}</div>
      <div className="appt-list-info">
        <p className="appt-list-name" style={{ margin: 0 }}>{ticket.subject} <span style={{ fontWeight: 400, color: '#888780' }}>#{ticket.id}</span></p>
        <p className="appt-list-time">
          {ticket.patient_name} · {formatDateTime(ticket.last_message_at || ticket.created_at)}
        </p>
      </div>
    </button>
  );
}

function MessageBubble({ msg, isDoctor }) {
  return (
    <div style={{ display: 'flex', justifyContent: isDoctor ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div
        style={{
          maxWidth: '75%',
          padding: '10px 14px',
          borderRadius: 14,
          borderBottomRightRadius: isDoctor ? 4 : 14,
          borderBottomLeftRadius: isDoctor ? 14 : 4,
          background: isDoctor ? '#0F4C75' : '#F2F3F4',
          color: isDoctor ? 'white' : '#222',
        }}
      >
        <p style={{ margin: 0, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
        <p style={{ margin: '4px 0 0', fontSize: 10.5, opacity: 0.7, textAlign: 'right' }}>
          {formatDateTime(msg.created_at)}
        </p>
      </div>
    </div>
  );
}

function SupportTicketDetail({ ticketId, token, onStatusChanged }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmStatus, setShowConfirmStatus] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchTicketMessages(token, ticketId)
      .then(res => {
        setTicket(res.ticket);
        setMessages(res.messages);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, ticketId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  async function handleSend() {
    if (!draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await addDoctorMessage(token, ticketId, { message: draft });
      setMessages(res.messages);
      setDraft('');
    } catch (err) {
      setError(err.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  }

  function handleStatusToggleClick() {
    setShowConfirmStatus(true);
  }

  async function handleStatusToggle() {
    const nextStatus = ticket.status === 'resolved' ? 'open' : 'resolved';
    setUpdatingStatus(true);
    setError(null);
    try {
      const updated = await setTicketStatus(token, ticketId, { status: nextStatus });
      setTicket(prev => ({ ...prev, status: updated.status }));
      setShowConfirmStatus(false);
      if (onStatusChanged) onStatusChanged(updated);
    } catch (err) {
      setError(err.message || 'Could not update status');
      setShowConfirmStatus(false);
    } finally {
      setUpdatingStatus(false);
    }
  }

  function handleCancelStatusConfirm() {
    if (updatingStatus) return;
    setShowConfirmStatus(false);
  }

  if (loading) return <div className="loading-row"><Loader2 size={16} className="spin" /> Loading conversation…</div>;
  if (!ticket) return <p className="error-text">{error || 'Ticket not found'}</p>;

  return (
    <div className="detail-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="detail-header">
        <div className="detail-avatar">{ticket.patient_name?.[0] || '?'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="detail-name" style={{ margin: 0 }}>{ticket.patient_name}</h2>
          <p className="detail-meta">
            {ticket.patient_email && <><Mail size={12} /> {ticket.patient_email} · </>}
            Ticket #{ticket.id}
          </p>
        </div>
        <span className={`status-pill status-${ticket.status}`}>{ticket.status}</span>
      </div>

      <div className="detail-section">
        <label className="field-label">Subject</label>
        <p className="row-desc" style={{ fontWeight: 600 }}>{ticket.subject}</p>
      </div>

      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '12px 4px', minHeight: 220, maxHeight: 420, border: '1px solid #EEF0F2', borderRadius: 10, marginBottom: 12 }}
      >
        {messages.length === 0 ? (
          <p className="muted-text" style={{ textAlign: 'center' }}>No messages yet.</p>
        ) : (
          messages.map(m => <MessageBubble key={m.id} msg={m} isDoctor={m.sender_type === 'doctor'} />)
        )}
      </div>

      {error && <p className="error-text">{error}</p>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          className="field-input"
          rows={2}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Type a message…"
          disabled={sending}
          style={{ flex: 1, resize: 'none' }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button onClick={handleSend} disabled={!draft.trim() || sending} className="btn-primary">
          <Send size={15} />
        </button>
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          onClick={handleStatusToggleClick}
          disabled={updatingStatus}
          className="btn-primary"
          style={
            ticket.status === 'resolved'
              ? undefined
              : { background: 'white', color: '#0F4C75', border: '1px solid #0F4C75' }
          }
        >
          <CheckCircle2 size={15} />
          {updatingStatus ? 'Updating…' : ticket.status === 'resolved' ? 'Reopen ticket' : 'Mark resolved'}
        </button>
      </div>

      {showConfirmStatus && (
        <ConfirmModal
          title={ticket.status === 'resolved' ? 'Reopen this ticket?' : 'Mark this ticket resolved?'}
          message={
            ticket.status === 'resolved'
              ? 'The ticket will move back to your Open list.'
              : 'The ticket will move to Resolved and leave your Open list.'
          }
          confirmLabel={updatingStatus ? 'Updating…' : ticket.status === 'resolved' ? 'Yes, reopen' : 'Yes, mark resolved'}
          busy={updatingStatus}
          onConfirm={handleStatusToggle}
          onCancel={handleCancelStatusConfirm}
        />
      )}
    </div>
  );
}

function SupportView({ token }) {
  const [statusFilter, setStatusFilter] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchSupportTickets(token, statusFilter)
      .then(rows => {
        setItems(rows);
        setSelectedId(rows[0]?.id ?? null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, statusFilter]);

  useEffect(() => { load(); }, [load]);

  function handleStatusChanged(updated) {
    if (updated.status !== statusFilter) {
      setItems(prev => {
        const next = prev.filter(t => t.id !== updated.id);
        setSelectedId(next[0]?.id ?? null);
        return next;
      });
    } else {
      setItems(prev => prev.map(t => (t.id === updated.id ? { ...t, status: updated.status } : t)));
    }
  }

  const selected = items.find(t => t.id === selectedId) || items[0];

  return (
    <div className="section-stack">
      <div className="timeline-filters">
        <button onClick={() => setStatusFilter('open')} className={`filter-chip ${statusFilter === 'open' ? 'active' : ''}`}>Open</button>
        <button onClick={() => setStatusFilter('resolved')} className={`filter-chip ${statusFilter === 'resolved' ? 'active' : ''}`}>Resolved</button>
      </div>

      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : items.length === 0 ? (
        <p className="muted-text">No {statusFilter} tickets.</p>
      ) : (
        <div className="master-detail">
          <div className="appt-list-col">
            {items.map(t => (
              <SupportTicketListItem key={t.id} ticket={t} active={t.id === selected.id} onClick={() => setSelectedId(t.id)} />
            ))}
          </div>
          <div className="detail-col">
            {selected && (
              <SupportTicketDetail
                key={selected.id}
                ticketId={selected.id}
                token={token}
                onStatusChanged={handleStatusChanged}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Patients
// ---------------------------------------------------------------------------
function PatientListItem({ patient, active, onClick }) {
  return (
    <button onClick={onClick} className={`appt-list-item ${active ? 'active' : ''}`}>
      <div className="appt-list-avatar">{patient.first_name?.[0] || '?'}</div>
      <div className="appt-list-info">
        <p className="appt-list-name" style={{ margin: 0 }}>{patient.first_name} {patient.last_name}</p>
        <p className="appt-list-time">{patient.email}</p>
      </div>
    </button>
  );
}

function PatientDetail({ patient, token }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    setLoadingHistory(true);
    fetchPatientAppointments(token, patient.id)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [token, patient.id]);

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <div className="detail-avatar">{patient.first_name?.[0] || '?'}</div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h2 className="detail-name" style={{ margin: 0 }}>{patient.first_name} {patient.last_name}</h2>
            <PatientReportsButton token={token} patientId={patient.id} patientName={`${patient.first_name} ${patient.last_name}`} />
            <PatientHealthButton token={token} patientId={patient.id} patientName={`${patient.first_name} ${patient.last_name}`} />
          </div>
          <p className="detail-meta"><Mail size={12} /> {patient.email}</p>
        </div>
      </div>

      {patient.phone && (
        <div className="detail-row">
          <span className="detail-label">Phone</span>
          <span className="detail-value">{formatPhoneDisplay({ phone: patient.phone, phoneCountryCode: patient.phone_country_code })}</span>
        </div>
      )}

      <div className="detail-section">
        <label className="field-label">Appointment history with you</label>
        {loadingHistory ? (
          <p className="muted-text" style={{ fontSize: 13 }}>Loading…</p>
        ) : history.length === 0 ? (
          <p className="muted-text" style={{ fontSize: 13 }}>No appointments yet.</p>
        ) : (
          <div className="list-stack">
            {history.map(h => (
              <div key={h.id} className="row-card">
                <div className="row-content">
                  <p className="row-title" style={{ marginBottom: 0 }}>{formatDate(h.appointment_date)} · {formatTime(h.appointment_time)}</p>
                </div>
                <span className={`status-pill status-${h.status}`}>{h.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReminderPanel token={token} patientId={patient.id} />
    </div>
  );
}

function PatientsView({ token }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback((q) => {
    setLoading(true);
    setError(null);
    fetchPatients(token, q)
      .then(rows => {
        setItems(rows);
        setSelectedId(prev => (rows.some(p => p.id === prev) ? prev : rows[0]?.id ?? null));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query), 300);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = items.find(p => p.id === selectedId) || items[0];

  return (
    <div className="section-stack">
      <div style={{ position: 'relative', maxWidth: 320 }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#888780' }} />
        <input
          className="field-input"
          style={{ paddingLeft: 32 }}
          placeholder="Search patients by name or email…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : items.length === 0 ? (
        <p className="muted-text">{query ? 'No patients match your search.' : 'No patients yet.'}</p>
      ) : (
        <div className="master-detail">
          <div className="appt-list-col">
            {items.map(p => (
              <PatientListItem key={p.id} patient={p} active={p.id === selected.id} onClick={() => setSelectedId(p.id)} />
            ))}
          </div>
          <div className="detail-col">
            {selected && <PatientDetail patient={selected} token={token} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Availability — FIX: renders weekly schedule dates, and handles structured
// 409 overlap conflicts from the server with a "sweet alert" dialog.
// ---------------------------------------------------------------------------
function AvailabilityView({ token, doctor, onGoToProfile }) {
  const tz = doctor?.effective_timezone;
  const tzLabelText = doctor?.effective_timezone_label;
  const [rules, setRules] = useState([]);
  const [dateGroups, setDateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState('single');

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('21:00');
  const [interval, setInterval] = useState('30');

  const DAY_OPTIONS = [
    { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
  ];
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]);
  const [weeklyStartTime, setWeeklyStartTime] = useState('09:00');
  const [weeklyEndTime, setWeeklyEndTime] = useState('21:00');
  const [weeklyInterval, setWeeklyInterval] = useState('30');
  const [weeksAhead, setWeeksAhead] = useState('4');

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [removingRuleId, setRemovingRuleId] = useState(null);
  const [showConfirmSingle, setShowConfirmSingle] = useState(false);
  const [showConfirmWeekly, setShowConfirmWeekly] = useState(false);

  // FIX: structured overlap-conflict state + "sweet alert" dialog
  const [conflict, setConflict] = useState(null);
  // conflict = { title, message, slotCount, ruleCount }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetchSlots(token)
      .then(data => {
        setRules(data?.weekly_rules || []);
        setDateGroups(data?.specific_dates || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function toggleDay(value) {
    setSelectedDays(prev =>
      prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value]
    );
  }

  function openConflictDialog(conflicts, isWeekly) {
    const slotCount = (conflicts?.existing_slots || []).length;
    const ruleCount = (conflicts?.weekly_rules || []).length;
    setConflict({
      title: 'Those slots overlap with existing availability',
      message: isWeekly
        ? 'Some of the slots this weekly schedule would create already exist. Please review the conflicting dates below.'
        : 'Some of these slots already exist. Please review the conflicting dates below.',
      slotCount,
      ruleCount,
      rawData: conflicts, // <--- ADD THIS LINE
    });
    setShowConfirmSingle(false);
    setShowConfirmWeekly(false);
  }

  // Opens the confirm popup instead of generating immediately.
  function handleGenerateSingleClick() {
    if (!date || !startTime || !endTime) return;
    setShowConfirmSingle(true);
  }

  // Only called after the user confirms — the real API call.
  async function handleGenerateSingle() {
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const response = await fetch(`${config.baseURL}/api/doctor-portal/slots/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slotDate: date, startTime, endTime, intervalMinutes: interval }),
      });
      const json = await response.json();

      if (response.status === 409 && json.conflicts) {
        openConflictDialog(json.conflicts, false);
        return;
      }
      if (!response.ok) throw new Error(json.error || 'Could not generate slots');

      setResult({ created: json.created, skipped: json.skipped });
      setShowConfirmSingle(false);
      await load();
    } catch (err) {
      setError(err.message || 'Could not generate slots');
      setShowConfirmSingle(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelSingleConfirm() {
    if (saving) return;
    setShowConfirmSingle(false);
  }

  function handleGenerateWeeklyClick() {
    if (selectedDays.length === 0 || !weeklyStartTime || !weeklyEndTime) return;
    setShowConfirmWeekly(true);
  }

  async function handleGenerateWeekly() {
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const response = await fetch(`${config.baseURL}/api/doctor-portal/slots/bulk-weekly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          daysOfWeek: selectedDays,
          startTime: weeklyStartTime,
          endTime: weeklyEndTime,
          intervalMinutes: weeklyInterval,
          weeksAhead,
        }),
      });
      const json = await response.json();

      if (response.status === 409 && json.conflicts) {
        openConflictDialog(json.conflicts, true);
        return;
      }
      if (!response.ok) throw new Error(json.error || 'Could not generate weekly slots');

      setResult({ created: json.created, datesCount: json.datesCount, note: json.note });
      setShowConfirmWeekly(false);
      await load();
    } catch (err) {
      setError(err.message || 'Could not generate weekly slots');
      setShowConfirmWeekly(false);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelWeeklyConfirm() {
    if (saving) return;
    setShowConfirmWeekly(false);
  }

  async function handleRemove(id) {
    setRemovingId(id);
    setError(null);
    try {
      await removeSlot(token, id);
      setDateGroups(prev =>
        prev
          .map(g => ({ ...g, slots: g.slots.filter(s => s.id !== id) }))
          .filter(g => g.slots.length > 0)
      );
    } catch (err) {
      setError(err.message || 'Could not remove slot');
    } finally {
      setRemovingId(null);
    }
  }

  async function handleRemoveRule(ruleId) {
    if (!window.confirm('Remove this weekly schedule? Any open slots it created will be deleted.')) return;
    setRemovingRuleId(ruleId);
    setError(null);
    try {
      await removeAvailabilityRule(token, ruleId);
      await load();
    } catch (err) {
      setError(err.message || 'Could not remove schedule');
    } finally {
      setRemovingRuleId(null);
    }
  }

  return (
    <div className="section-stack">
      {tz && (
        <div className="inline-form" style={{ maxWidth: 560, background: '#EAF3FA', border: '1px solid #C9DEEE' }}>
          <p style={{ margin: 0, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} style={{ flexShrink: 0 }} />
            <span>
              All times on this page are in <strong>{tzLabelText}</strong>.
            </span>
          </p>
          {doctor?.timezone_source !== 'saved' && (
            <p className="muted-text" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
              This is based on your country{doctor?.country ? ` (${doctor.country})` : ''}.{' '}
              <button
                type="button"
                onClick={onGoToProfile}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0F4C75', textDecoration: 'underline', font: 'inherit' }}
              >
                Confirm your timezone
              </button>{' '}
              so patients in other countries see the right times.
            </p>
          )}
          <p className="muted-text" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
            Patients see these slots converted to their own timezone.
          </p>
        </div>
      )}

      <div className="inline-form" style={{ maxWidth: 560 }}>
        <label className="field-label">How do you want to set availability?</label>
        <select className="field-input" value={mode} onChange={e => { setMode(e.target.value); setResult(null); setError(null); }}>
          <option value="single">Specific date</option>
          <option value="weekly">Weekly schedule (e.g. Mon–Fri)</option>
        </select>

        {mode === 'single' && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <div style={{ flex: '1 1 140px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>Date</label>
                <input type="date" className="field-input" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>From</label>
                <input type="time" className="field-input" value={startTime} onChange={e => setStartTime(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>To</label>
                <input type="time" className="field-input" value={endTime} onChange={e => setEndTime(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>Gap between slots</label>
                <select className="field-input" value={interval} onChange={e => setInterval(e.target.value)}>
                  <option value="15">15 minutes</option>
                  <option value="20">20 minutes</option>
                  <option value="25">25 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleGenerateSingleClick}
              disabled={!date || !startTime || !endTime || saving}
              className="btn-primary"
              style={{ alignSelf: 'flex-start' }}
            >
              <Plus size={15} /> {saving ? 'Generating…' : 'Generate slots'}
            </button>
          </>
        )}

        {mode === 'weekly' && (
          <>
            <div>
              <label className="field-label" style={{ fontSize: 11 }}>Repeat on</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAY_OPTIONS.map(d => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className="filter-chip"
                    style={selectedDays.includes(d.value)
                      ? { borderColor: '#0f4c75', backgroundColor: '#e6f1fb', color: '#0f4c75' }
                      : undefined}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button type="button" onClick={() => setSelectedDays([1, 2, 3, 4, 5])} className="filter-chip">Weekdays</button>
                <button type="button" onClick={() => setSelectedDays([0, 6])} className="filter-chip">Weekends</button>
                <button type="button" onClick={() => setSelectedDays([0, 1, 2, 3, 4, 5, 6])} className="filter-chip">Every day</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 100px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>From</label>
                <input type="time" className="field-input" value={weeklyStartTime} onChange={e => setWeeklyStartTime(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>To</label>
                <input type="time" className="field-input" value={weeklyEndTime} onChange={e => setWeeklyEndTime(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>Gap between slots</label>
                <select className="field-input" value={weeklyInterval} onChange={e => setWeeklyInterval(e.target.value)}>
                  <option value="15">15 minutes</option>
                  <option value="20">20 minutes</option>
                  <option value="25">25 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                </select>
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label className="field-label" style={{ fontSize: 11 }}>For how many weeks</label>
                <select className="field-input" value={weeksAhead} onChange={e => setWeeksAhead(e.target.value)}>
                  <option value="2">2 weeks</option>
                  <option value="4">4 weeks</option>
                  <option value="8">8 weeks</option>
                  <option value="12">12 weeks</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleGenerateWeeklyClick}
              disabled={selectedDays.length === 0 || !weeklyStartTime || !weeklyEndTime || saving}
              className="btn-primary"
              style={{ alignSelf: 'flex-start' }}
            >
              <Plus size={15} /> {saving ? 'Generating…' : 'Generate weekly schedule'}
            </button>
          </>
        )}

        {result && (
          <p className="muted-text" style={{ fontSize: 13 }}>
            {mode === 'single'
              ? `Added ${result.created} slot${result.created === 1 ? '' : 's'}${result.skipped > 0 ? ` (${result.skipped} already existed and were skipped)` : ''}.`
              : `Added ${result.created} slot${result.created === 1 ? '' : 's'} across ${result.datesCount} date${result.datesCount === 1 ? '' : 's'}.${result.note ? ` ${result.note}` : ''}`}
          </p>
        )}
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading availability…</div>
      ) : rules.length === 0 && dateGroups.length === 0 ? (
        <p className="muted-text">No availability set yet — use the form above to add some.</p>
      ) : (
        <>
          {/* Weekly schedules — FIX: now show the date range the rule covers */}
          {rules.length > 0 && (
            <div className="list-stack">
              {rules.map(rule => (
                <div key={rule.id} className="rule-card">
                  <div className="rule-info">
                    <p className="rule-title">
                      {rule.title}
                      {rule.starts_on && rule.ends_on && (
                        <span style={{ fontSize: 13.5, fontWeight: 400, color: '#666', marginLeft: 8 }}>
                          ({formatDate(rule.starts_on)} – {formatDate(rule.ends_on)})
                        </span>
                      )}
                    </p>
                    <p className="rule-meta">
                      {rule.subtitle}
                      {rule.booked_slots > 0 ? ` · ${rule.booked_slots} booked` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveRule(rule.rule_id)}
                    disabled={removingRuleId === rule.rule_id}
                    className="rule-delete-btn"
                    aria-label="Remove weekly schedule"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Specific dates */}
          {dateGroups.length > 0 && (
            <div className="list-stack">
              {dateGroups.map(group => (
                <div key={group.date} className="avail-day-group">
                  <p className="avail-day-heading">{group.label}</p>
                  <div className="avail-slot-row">
                    {group.slots.map(s => (
                      <span key={s.id} className={`avail-slot-chip ${s.is_booked ? 'booked' : ''}`}>
                        {s.display_time}
                        {s.is_booked ? (
                          <span className="avail-booked-tag">Booked</span>
                        ) : (
                          <button
                            onClick={() => handleRemove(s.id)}
                            disabled={removingId === s.id}
                            className="avail-remove-btn"
                            aria-label="Remove slot"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showConfirmSingle && (
        <ConfirmModal
          title="Generate these slots?"
          message={`${date ? formatDate(date) : ''} from ${startTime} to ${endTime}, every ${interval} minutes.`}
          confirmLabel={saving ? 'Generating…' : 'Yes, generate slots'}
          busy={saving}
          onConfirm={handleGenerateSingle}
          onCancel={handleCancelSingleConfirm}
        />
      )}

      {showConfirmWeekly && (
        <ConfirmModal
          title="Generate this weekly schedule?"
          message={`${selectedDays
            .map(d => DAY_OPTIONS.find(o => o.value === d)?.label)
            .filter(Boolean)
            .join(', ')} · ${weeklyStartTime}–${weeklyEndTime}, every ${weeklyInterval} minutes, for the next ${weeksAhead} week${weeksAhead === '1' ? '' : 's'}.`}
          confirmLabel={saving ? 'Generating…' : 'Yes, generate schedule'}
          busy={saving}
          onConfirm={handleGenerateWeekly}
          onCancel={handleCancelWeeklyConfirm}
        />
      )}

      {/* FIX: "sweet alert" style overlap dialog — alert-only, single OK button */}
      {conflict && (
        <ConfirmModal
          title={conflict.title}
          message={conflict.message}
          hideConfirm
          cancelLabel="OK, I'll adjust"
          details={
            <div style={{ fontSize: 13.5, color: '#3A3A38', maxHeight: 250, overflowY: 'auto' }}>
              <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Conflicting dates:</p>
              <ul style={{ paddingLeft: 20, margin: '0 0 16px' }}>
                {/* Extract unique dates from both existing slots and weekly rules */}
                {[...new Set([
                  ...(conflict.rawData?.existing_slots || []),
                  ...(conflict.rawData?.weekly_rules || [])
                ].map(c => c.date))]
                  .sort()
                  .map(date => (
                    <li key={date} style={{ marginBottom: 4 }}>
                      {formatDate(date)}
                    </li>
                  ))
                }
              </ul>
              <p style={{ margin: 0, color: '#888780', fontSize: 13 }}>
                Review your existing weekly schedules and specific dates, remove what's in the way, then try again.
              </p>
            </div>
          }
          onCancel={() => setConflict(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cover (doctor-initiated delegation): invites + "Covering for Dr. X" workspace
// ---------------------------------------------------------------------------
const fetchColleagues = token => apiRequest('/doctor-delegations/colleagues', token);
const fetchSentDelegations = token => apiRequest('/doctor-delegations/sent', token);
const fetchReceivedDelegations = token => apiRequest('/doctor-delegations/received', token);
const createDelegation = (token, body) =>
  apiRequest('/doctor-delegations', token, { method: 'POST', body: JSON.stringify(body) });
const answerDelegation = (token, id, action) =>
  apiRequest(`/doctor-delegations/${id}/${action}`, token, { method: 'PUT' });
const fetchCoverToken = (token, id) =>
  apiRequest(`/doctor-delegations/${id}/cover-token`, token, { method: 'POST' });

const STATE_PILL = {
  pending: 'status-open',
  active: 'status-confirmed',
  scheduled: 'status-completed',
};
function coverRange(d) {
  return `${d.start_date ? formatDate(d.start_date) : 'Now'} → ${d.end_date ? formatDate(d.end_date) : 'Until revoked'}`;
}

function CoverInvitesView({ token, received, sent, onChanged }) {
  const [colleagues, setColleagues] = useState([]);
  const [form, setForm] = useState({ delegateDoctorId: '', startDate: '', endDate: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [confirm, setConfirm] = useState(null); // { id, action, title, message, label, danger }
  const [conflict, setConflict] = useState(null); // { message, conflict } from a 409 COVER_CONFLICT

  useEffect(() => {
    fetchColleagues(token).then(setColleagues).catch(() => setColleagues([]));
  }, [token]);

  const pending = received.filter(r => r.state === 'pending');
  const covering = received.filter(r => r.state === 'active' || r.state === 'scheduled');
  const mySent = sent.filter(r => ['pending', 'active', 'scheduled'].includes(r.state));
  const history = sent.filter(r => !['pending', 'active', 'scheduled'].includes(r.state)).slice(0, 5);

  async function handleSend(e) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    if (!form.delegateDoctorId) { setError('Please choose a doctor.'); return; }
    setSaving(true);
    try {
      await createDelegation(token, {
        delegateDoctorId: Number(form.delegateDoctorId),
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        message: form.message || undefined,
      });
      setForm({ delegateDoctorId: '', startDate: '', endDate: '', message: '' });
      setOkMsg('Invite sent. They will be notified in their dashboard and by email.');
      onChanged();
    } catch (err) {
      if (err.body?.code === 'COVER_CONFLICT') setConflict(err.body);
      else setError(err.message || 'Could not send the invite');
    } finally {
      setSaving(false);
    }
  }

  async function runAction(id, action) {
    setBusyId(id);
    setError(null);
    setOkMsg(null);
    try {
      await answerDelegation(token, id, action);
      setConfirm(null);
      onChanged();
    } catch (err) {
      if (err.body?.code === 'COVER_CONFLICT') setConflict(err.body);
      else setError(err.message || 'Something went wrong');
      setConfirm(null);
    } finally {
      setBusyId(null);
    }
  }

  const DoctorLine = ({ name, specialty, photo }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar className="appt-list-avatar" name={name} photoUrl={photo} />
      <div>
        <p className="row-title" style={{ marginBottom: 0 }}>{name}</p>
        <p className="row-meta">{specialty}</p>
      </div>
    </div>
  );

  return (
    <div className="section-stack">
      {error && <p className="error-text">{error}</p>}
      {okMsg && (
        <div role="status" style={{ padding: '10px 14px', background: '#E7F6EC', border: '1px solid #9BD4AE', borderRadius: 8, color: '#1B6B3A', fontSize: 13 }}>
          {okMsg}
        </div>
      )}

      {pending.length > 0 && (
        <div className="detail-panel">
          <label className="field-label"><Bell size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> Invites for you</label>
          <div className="list-stack">
            {pending.map(r => (
              <div key={r.id} className="row-card" style={{ flexWrap: 'wrap', gap: 10 }}>
                <div className="row-content">
                  <DoctorLine name={r.owner_name} specialty={r.owner_specialty} photo={r.owner_photo_url} />
                  <p className="row-meta" style={{ marginTop: 6 }}>
                    Asks you to cover their appointments · {coverRange(r)}
                  </p>
                  {r.message && <p className="row-desc">"{r.message}"</p>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" disabled={busyId === r.id} onClick={() => runAction(r.id, 'accept')}>
                    <CheckCircle2 size={14} /> Accept
                  </button>
                  <button className="btn-danger" disabled={busyId === r.id} onClick={() => runAction(r.id, 'decline')}>
                    <XCircle size={14} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {covering.length > 0 && (
        <div className="detail-panel">
          <label className="field-label">You are covering for</label>
          <div className="list-stack">
            {covering.map(r => (
              <div key={r.id} className="row-card" style={{ flexWrap: 'wrap', gap: 10 }}>
                <div className="row-content">
                  <DoctorLine name={r.owner_name} specialty={r.owner_specialty} photo={r.owner_photo_url} />
                  <p className="row-meta" style={{ marginTop: 6 }}>{coverRange(r)}</p>
                </div>
                <span className={`status-pill ${STATE_PILL[r.state]}`}>{r.state}</span>
                <button
                  className="btn-danger"
                  disabled={busyId === r.id}
                  onClick={() => setConfirm({
                    id: r.id, action: 'revoke', danger: true, label: 'Yes, stop covering',
                    title: `Stop covering for ${r.owner_name}?`,
                    message: 'You will lose access to their appointments, slots, patients and support tickets.',
                  })}
                >
                  Stop covering
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="detail-panel">
        <label className="field-label"><UserCheck size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> I'm away — ask a colleague to cover</label>
        <p className="muted-text" style={{ fontSize: 12.5, marginBottom: 10 }}>
          Once they accept, they can manage your appointments, availability, patients and support tickets
          (not your profile) until the end date, or until you revoke it.
        </p>
        <form onSubmit={handleSend}>
          <div className="detail-section">
            <label className="field-label">Colleague</label>
            <select
              className="field-input"
              value={form.delegateDoctorId}
              onChange={e => setForm(f => ({ ...f, delegateDoctorId: e.target.value }))}
            >
              <option value="">{colleagues.length ? 'Select a doctor' : 'No colleagues in your group yet'}</option>
              {colleagues.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.specialty ? ` — ${c.specialty}` : ''}</option>
              ))}
            </select>
            {colleagues.length === 0 && (
              <p className="muted-text" style={{ fontSize: 12, marginTop: 6 }}>
                You can only invite doctors in your group. Ask the admin to add you to a group.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="detail-section" style={{ flex: '1 1 160px' }}>
              <label className="field-label">From (optional)</label>
              <input type="date" className="field-input" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div className="detail-section" style={{ flex: '1 1 160px' }}>
              <label className="field-label">Until (optional)</label>
              <input type="date" className="field-input" value={form.endDate} min={form.startDate || undefined}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>
          </div>
          <p className="muted-text" style={{ fontSize: 12, margin: '0 0 8px' }}>Leave both blank for open-ended cover that starts as soon as they accept.</p>
          <div className="detail-section">
            <label className="field-label">Note (optional)</label>
            <input className="field-input" maxLength={255} placeholder="e.g. Away on leave, please cover my patients"
              value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
          </div>
          <button type="submit" className="btn-primary" style={{ marginTop: 16 }} disabled={saving}>
  <Send size={14} /> {saving ? 'Sending…' : 'Send invite'}
</button>
        </form>
      </div>

      {(mySent.length > 0 || history.length > 0) && (
        <div className="detail-panel">
          <label className="field-label">Your invites</label>
          <div className="list-stack">
            {[...mySent, ...history].map(r => {
              const live = ['pending', 'active', 'scheduled'].includes(r.state);
              return (
                <div key={r.id} className="row-card" style={{ flexWrap: 'wrap', gap: 10 }}>
                  <div className="row-content">
                    <DoctorLine name={r.delegate_name} specialty={r.delegate_specialty} photo={r.delegate_photo_url} />
                    <p className="row-meta" style={{ marginTop: 6 }}>{coverRange(r)}</p>
                  </div>
                  <span className={`status-pill ${STATE_PILL[r.state] || 'status-cancelled'}`}>{r.state}</span>
                  {live && (
                    <button
                      className="btn-danger"
                      disabled={busyId === r.id}
                      onClick={() => setConfirm({
                        id: r.id, action: 'revoke', danger: true,
                        label: r.state === 'pending' ? 'Yes, cancel invite' : 'Yes, revoke access',
                        title: r.state === 'pending' ? 'Cancel this invite?' : `Revoke ${r.delegate_name}'s access?`,
                        message: r.state === 'pending'
                          ? 'They will no longer be able to accept it.'
                          : 'They will immediately lose access to your appointments, slots, patients and support tickets.',
                      })}
                    >
                      {r.state === 'pending' ? 'Cancel' : 'Revoke'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {conflict && (
        <ConfirmModal
          title="Can't do this — dates overlap"
          message={
            conflict.conflict?.state === 'pending'
              ? `${conflict.conflict.doctorName} has already asked you to cover for them, and that invite is still open.`
              : `You are already covering for ${conflict.conflict?.doctorName || 'this doctor'}.`
          }
          details={
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Existing cover</div>
              <div>{conflict.conflict?.doctorName}</div>
              <div style={{ color: '#5F5E5A', marginTop: 2 }}>
                {coverRange({ start_date: conflict.conflict?.startDate, end_date: conflict.conflict?.endDate })}
                {conflict.conflict?.state ? ` · ${conflict.conflict.state}` : ''}
              </div>
              <div style={{ color: '#5F5E5A', marginTop: 8 }}>
                Two doctors cannot cover each other at the same time. Choose different dates or another colleague.
              </div>
            </div>
          }
          cancelLabel="OK"
          hideConfirm
          onCancel={() => setConflict(null)}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.label}
          danger={confirm.danger}
          busy={busyId === confirm.id}
          onConfirm={() => runAction(confirm.id, confirm.action)}
          onCancel={() => { if (busyId == null) setConfirm(null); }}
        />
      )}
    </div>
  );
}

// "Covering for Dr. A" workspace. A cover token behaves exactly like Dr. A's own token on every
// doctor-portal endpoint (and is re-checked against the delegation on each request), so the
// existing views below are reused as-is.
const COVER_SECTIONS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'patients', label: 'Patients' },
  { id: 'availability', label: 'Availability' },
  { id: 'support', label: 'Support' },
];

function CoverView({ token, delegation, onEnded }) {
  const [coverToken, setCoverToken] = useState(null);
  const [owner, setOwner] = useState(null);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('upcoming');

  useEffect(() => {
    let alive = true;
    setCoverToken(null);
    setOwner(null);
    setError(null);
    fetchCoverToken(token, delegation.id)
      .then(r => { if (alive) setCoverToken(r.token); })
      .catch(err => {
        if (!alive) return;
        setError(err.message);
        if (err.status === 403 && onEnded) onEnded();
      });
    return () => { alive = false; };
  }, [token, delegation.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coverToken) return;
    fetchProfile(coverToken).then(setOwner).catch(() => {});
  }, [coverToken]);

  if (error) return <p className="error-text">{error}</p>;
  if (!coverToken) return <div className="loading-row"><Loader2 size={16} className="spin" /> Opening {delegation.owner_name}'s workspace…</div>;

  return (
    <div className="section-stack">
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 14px', background: '#EAF3FB', border: '1px solid #B9D6EE',
          borderRadius: 8, color: '#0F4C75', fontSize: 13,
        }}
      >
        <UserCheck size={15} style={{ flexShrink: 0 }} />
        <span>
          You are covering for <strong>{delegation.owner_name}</strong> ({coverRange(delegation)}).
          Anything you change here applies to their appointments, slots, patients and support tickets.
        </span>
      </div>

      <div className="timeline-filters">
        {COVER_SECTIONS.map(c => (
          <button key={c.id} onClick={() => setSection(c.id)} className={`filter-chip ${section === c.id ? 'active' : ''}`}>
            {c.label}
          </button>
        ))}
      </div>

      {section === 'upcoming' && <AppointmentsView key="c-up" token={coverToken} statusFilter="confirmed" />}
      {section === 'pending' && <AppointmentsView key="c-pe" token={coverToken} statusFilter="pending" />}
      {section === 'completed' && <AppointmentsView key="c-co" token={coverToken} statusFilter="completed" />}
      {section === 'patients' && <PatientsView token={coverToken} />}
      {section === 'support' && <SupportView token={coverToken} />}
      {section === 'availability' && (
        owner
          ? <AvailabilityView token={coverToken} doctor={owner} onGoToProfile={() => {}} />
          : <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// My nurses — nurses linked to me. They can read my patients' history but cannot
// confirm, cancel or change anything (enforced on the server).
// ---------------------------------------------------------------------------
function NursesView({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null); // { name, link, emailSent }
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: 'toggle' | 'reinvite' | 'remove', nurse }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchNurses(token)
      .then(setItems)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    setError(null);
    setInvite(null);
    try {
      const res = await createNurse(token, form);
      setInvite({ name: form.name, email: form.email, nurseId: res.id, link: res.inviteLink, emailSent: res.emailSent });
      setForm({ name: '', email: '', phone: '' });
      load();
    } catch (err) {
      setError(err.message || 'Could not create the nurse');
    } finally {
      setSaving(false);
    }
  }

  async function runConfirmed() {
    if (!confirm) return;
    const { type, nurse } = confirm;
    setBusy(true);
    setError(null);
    try {
      if (type === 'toggle') {
        await setNurseAccess(token, nurse.id, { is_active: nurse.access_active ? 0 : 1 });
      } else if (type === 'reinvite') {
        const res = await reinviteNurse(token, nurse.id);
        setInvite({ name: nurse.name, email: nurse.email, nurseId: nurse.id, link: res.inviteLink, emailSent: res.emailSent });
      } else if (type === 'remove') {
        await removeNurse(token, nurse.id);
      }
      setConfirm(null);
      load();
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard blocked: the link is shown in the box to copy by hand */ }
  }

  // The server builds the link from USER_SITE_URL; if that is not set it sends just the path,
  // so complete it with this website's address (this dashboard is on the same site).
  const fullLink = invite ? (invite.link.startsWith('/') ? `${window.location.origin}${invite.link}` : invite.link) : '';
  const shareText = invite
    ? `Hello ${invite.name}, you have been added as a nurse on Future Family. Set your password here (valid 48 hours, one use): ${fullLink}`
    : '';
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailHref = invite && invite.email
    ? `mailto:${invite.email}?subject=${encodeURIComponent('Set up your Future Family nurse account')}&body=${encodeURIComponent(shareText)}`
    : '#';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // One look for every share action (links and buttons), so they line up and match the card.
  const shareBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: '1px solid #9BD4AE', background: '#FFFFFF',
    color: '#1B6B3A', fontSize: 13, fontWeight: 600, lineHeight: 1.2, fontFamily: 'inherit',
    textDecoration: 'none', cursor: 'pointer',
  };

  async function shareLink() {
    try {
      await navigator.share({ title: 'Future Family nurse account', text: shareText });
    } catch (e) { /* the person closed the share sheet */ }
  }

  async function resendEmail() {
    if (!invite || !invite.nurseId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reinviteNurse(token, invite.nurseId);
      setInvite({ ...invite, link: res.inviteLink, emailSent: res.emailSent });
    } catch (err) {
      setError(err.message || 'Could not send the email');
    } finally {
      setBusy(false);
    }
  }

  const confirmText = {
    toggle: confirm?.nurse?.access_active
      ? { title: 'Pause access?', message: `${confirm?.nurse?.name} will no longer see your patients. Their access to other doctors is not affected.`, label: 'Yes, pause' }
      : { title: 'Allow access?', message: `${confirm?.nurse?.name} will be able to read your patients' history again.`, label: 'Yes, allow' },
    reinvite: { title: 'Send a new link?', message: 'The old link stops working. Use this if the nurse forgot their password or never set one.', label: 'Yes, send' },
    remove: { title: 'Remove this nurse?', message: `${confirm?.nurse?.name} will lose access to your patients.`, label: 'Yes, remove', danger: true },
  }[confirm?.type] || {};

  return (
    <div className="section-stack">
      <div className="detail-panel" style={{ maxWidth: 640 }}>
        <label className="field-label"><Users size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> Add a nurse</label>
        <p className="muted-text" style={{ fontSize: 13, margin: '4px 0 12px' }}>
          A nurse can read your patients' history and appointments. They cannot confirm, complete or cancel anything.
          They get a link to set their own password.
        </p>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <input className="field-input" placeholder="Full name" value={form.name} maxLength={150}
              onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <input className="field-input" type="email" placeholder="Email" value={form.email} maxLength={190}
              onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <input className="field-input" placeholder="Phone (optional)" value={form.phone} maxLength={40}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <button type="submit" disabled={saving || !form.name.trim() || !form.email.trim()} className="btn-primary">
            <Plus size={14} /> {saving ? 'Adding…' : 'Add nurse'}
          </button>
        </form>

        {invite && (
          <div
            style={{
              marginTop: 14, padding: '10px 14px', background: '#E7F6EC', border: '1px solid #9BD4AE',
              borderRadius: 8, color: '#1B6B3A', fontSize: 13,
            }}
          >
            <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
              {invite.emailSent
                ? `Link emailed to ${invite.email}.`
                : `The email to ${invite.email} could not be sent. Send the link yourself below.`}
            </p>
            <p style={{ margin: '0 0 8px' }}>It works for 48 hours and can be used once. You can also send it directly:</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <input className="field-input" readOnly value={fullLink} onFocus={e => e.target.select()} style={{ flex: 1 }} />
              <button type="button" onClick={copyLink} className="btn-primary">{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <a href={waHref} target="_blank" rel="noreferrer" style={shareBtn}>
                <MessageCircle size={15} /> WhatsApp
              </a>
              <a href={mailHref} style={shareBtn}>
                <Mail size={15} /> Email app
              </a>
              {canShare && (
                <button type="button" onClick={shareLink} style={shareBtn}>
                  <Send size={15} /> Share
                </button>
              )}
              <button type="button" onClick={resendEmail} disabled={busy} style={{ ...shareBtn, opacity: busy ? 0.6 : 1 }}>
                <Mail size={15} /> {busy ? 'Sending…' : 'Email again'}
              </button>
              <button
                type="button"
                onClick={() => setInvite(null)}
                style={{ ...shareBtn, marginLeft: 'auto', background: 'transparent', border: '1px solid transparent', color: '#4B5563' }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="muted-text">No nurses yet. Add one above.</p>
      ) : (
        <div className="list-stack">
          {items.map(n => {
            return (
              <div key={n.id} className="row-card" style={{ flexWrap: 'wrap', gap: 10 }}>
                <div className="row-content" style={{ minWidth: 200 }}>
                  <p className="row-title" style={{ marginBottom: 2 }}>{n.name}</p>
                  <p className="row-meta"><Mail size={11} /> {n.email}{n.phone ? ` · ${n.phone}` : ''}</p>
                  <p className="row-meta">
                    {n.has_password
                      ? (n.last_login_at ? `Last login ${formatDateTime(n.last_login_at)}` : 'Has not logged in yet')
                      : 'Has not set a password yet'}
                  </p>
                </div>

                {!n.nurse_active ? (
                  <span className="status-pill status-cancelled" style={{ fontSize: 11 }}>Switched off by admin</span>
                ) : !n.access_active ? (
                  <span className="status-pill status-cancelled" style={{ fontSize: 11 }}>Access paused</span>
                ) : !n.has_password ? (
                  <span className="status-pill status-pending" style={{ fontSize: 11 }}>Invite not accepted</span>
                ) : (
                  <span className="status-pill status-confirmed" style={{ fontSize: 11 }}>Active</span>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {n.nurse_active ? (
                    <button type="button" className="btn-primary"
                      style={{ background: 'white', color: '#0F4C75', border: '1px solid #0F4C75' }}
                      onClick={() => setConfirm({ type: 'toggle', nurse: n })}>
                      {n.access_active ? 'Pause access' : 'Allow access'}
                    </button>
                  ) : null}
                  {n.created_by_me && (
                    <button type="button" className="btn-primary"
                      style={{ background: 'white', color: '#0F4C75', border: '1px solid #0F4C75' }}
                      onClick={() => setConfirm({ type: 'reinvite', nurse: n })}>
                      {n.has_password ? 'Reset password link' : 'Send new link'}
                    </button>
                  )}
                  <button type="button" className="btn-danger" onClick={() => setConfirm({ type: 'remove', nurse: n })}>
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title={confirmText.title}
          message={confirmText.message}
          confirmLabel={busy ? 'Working…' : confirmText.label}
          danger={Boolean(confirmText.danger)}
          busy={busy}
          onConfirm={runConfirmed}
          onCancel={() => { if (!busy) setConfirm(null); }}
        />
      )}
    </div>
  );
}

const DoctorDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const doctorState = useSelector((state) => state.doctor);
  const token = doctorState?.token || localStorage.getItem('doctor_token');

  const [doctor, setDoctorProfile] = useState(doctorState?.doctorInfo || null);
  const [tab, setTab] = useState('upcoming');
  const [loadError, setLoadError] = useState(null);
  // Admin switched this doctor OFF (doctors.is_active = 0): show a notice
  // instead of the dashboard, so no slots / appointment lists are loaded.
  const [inactive, setInactive] = useState(false);

  // Cover invites (I'm asked to cover) and the invites I sent
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);

  const loadDelegations = useCallback(() => {
    if (!token) return;
    fetchReceivedDelegations(token).then(setReceived).catch(() => {});
    fetchSentDelegations(token).then(setSent).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/doctor/login', { replace: true });
      return;
    }
    fetchProfile(token)
      .then((profile) => {
        if (profile && Number(profile.is_active) === 0) setInactive(true);
        else setDoctorProfile(profile);
      })
      .catch((err) => {
        if (err.body?.code === 'DOCTOR_INACTIVE') setInactive(true);
        else setLoadError(err.message);
      });
  }, [token, navigate]);

  // Load invites now, then every minute so a new invite shows up (badge + banner) without a reload
  useEffect(() => {
    if (!token || inactive) return undefined;
    loadDelegations();
    const t = setInterval(loadDelegations, 60000);
    return () => clearInterval(t);
  }, [token, inactive, loadDelegations]);

  const pendingInvites = received.filter((r) => r.state === 'pending');
  const coverTabs = received.filter((r) => r.state === 'active');
  const activeCover = tab.startsWith('cover:')
    ? coverTabs.find((c) => String(c.id) === tab.slice(6))
    : null;

  // The cover I was viewing ended / was revoked -> go back to Cover & Invites
  useEffect(() => {
    if (tab.startsWith('cover:') && !activeCover) setTab('cover');
  }, [tab, activeCover]);

  async function handleLogout() {
    try {
      await apiRequest('/doctor-auth/logout', token, { method: 'POST' });
    } catch (err) {
      console.warn('Doctor logout endpoint failed (logging out locally anyway):', err.message);
    }
    dispatch(clearDoctor());
    localStorage.removeItem('doctor_token');
    navigate('/doctor/login', { replace: true });
  }

  if (!token) return null;

  if (inactive) {
    return (
      <div className="dd-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 16 }}>
          <AlertCircle size={32} style={{ color: '#B42318', marginBottom: 8 }} />
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Your profile is inactive</h2>
          <p className="muted-text" style={{ marginBottom: 16 }}>
            Your account has been switched off, so your appointments, patients and availability are hidden.
            Please contact the admin to have it reactivated.
          </p>
          <button onClick={handleLogout} className="btn-primary">Log out</button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="dd-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p className="error-text" style={{ marginBottom: 12 }}>{loadError}</p>
          <button onClick={handleLogout} className="btn-primary">Back to login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dd-page">
      <Sidebar
        doctor={doctor}
        activeTab={tab}
        onTabChange={setTab}
        onLogout={handleLogout}
        coverTabs={coverTabs}
        pendingInvites={pendingInvites.length}
      />

      <main className="dd-main">
        <div className="dd-main-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="dd-main-title">
            {tab === 'upcoming' && 'Upcoming appointments'}
            {tab === 'pending' && 'Pending bookings'}
            {tab === 'completed' && 'Completed visits'}
            {tab === 'support' && 'Support inbox'}
            {tab === 'patients' && 'Patients'}
            {tab === 'availability' && 'My availability'}
            {tab === 'cover' && 'Cover & invites'}
            {tab === 'nurses' && 'My nurses'}
            {tab === 'profile' && 'My profile'}
            {activeCover && `Covering for ${activeCover.owner_name}`}
          </h1>
          <AlertsBell token={token} />
        </div>

        {pendingInvites.length > 0 && tab !== 'cover' && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '10px 14px', margin: '0 0 12px', background: '#FFF4E5',
              border: '1px solid #F3C98B', borderRadius: 8, color: '#8A5A00', fontSize: 13,
            }}
          >
            <Bell size={15} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              {pendingInvites.length === 1
                ? `${pendingInvites[0].owner_name} has asked you to cover their appointments.`
                : `${pendingInvites.length} doctors have asked you to cover their appointments.`}
            </span>
            <button className="btn-primary" onClick={() => setTab('cover')}>View invite</button>
          </div>
        )}

        <div className="dd-main-content">
          {tab === 'upcoming' && <AppointmentsView token={token} statusFilter="confirmed" />}
          {tab === 'pending' && <AppointmentsView token={token} statusFilter="pending" />}
          {tab === 'completed' && <AppointmentsView token={token} statusFilter="completed" />}
          {tab === 'support' && <SupportView token={token} />}
          {tab === 'patients' && <PatientsView token={token} />}
          {tab === 'availability' && <AvailabilityView token={token} doctor={doctor} onGoToProfile={() => setTab('profile')} />}
          {tab === 'cover' && (
            <CoverInvitesView token={token} received={received} sent={sent} onChanged={loadDelegations} />
          )}
          {tab === 'nurses' && <NursesView token={token} />}
          {tab === 'profile' && <ProfileView doctor={doctor} token={token} onUpdated={setDoctorProfile} />}
          {activeCover && (
            <CoverView key={activeCover.id} token={token} delegation={activeCover} onEnded={loadDelegations} />
          )}
        </div>
      </main>
    </div>
  );
};

export default DoctorDashboard;